/**
 * File: select_new_article_title.tsx
 * Description: Controlled text input for entering a new FAQ article title in the admin interface.
 * Displays a validation error when the field has been touched and left empty.
 */
import { useState } from 'react';
import TextField from '@mui/material/TextField';
import type { Category } from '@/interfaces/category';

/**
 * Props for the SelectNewArticleTitle component.
 *
 * @property {string} value - The current title value.
 * @property {(title: string) => void} onChange - Callback invoked with the new value on every keystroke.
 * @property {Category | null} selectedCategory - The category currently selected, used to check for duplicate titles.
 */
interface SelectNewArticleTitleProps {
  value: string;
  onChange: (title: string) => void;
  selectedCategory: Category | null;
}

/**
 * Text field component that collects a title for a new FAQ entry.
 * Tracks whether the user has interacted with the field and shows an error
 * message if it is left blank after being touched.
 *
 * @param {string} props.value - The current title value.
 * @param {(title: string) => void} props.onChange - Callback invoked with the new value on every keystroke.
 * @returns {JSX.Element}
 */
function SelectNewArticleTitle({ value, onChange, selectedCategory }: SelectNewArticleTitleProps) {
  
  const [touched, setTouched] = useState(false);

  // Only surface the error after the user has interacted with the field at least once.
  const isError = touched && value.trim() === ''
  const existingArticle = selectedCategory?.articles.find((article) => article.title === value.trim());

  /**
   * Forwards the raw input value to the parent's onChange handler.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The native change event.
   */
  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    onChange(event.target.value);
  }

  /**
   * Marks the field as touched so validation feedback becomes visible.
   */
  function handleBlur() {
    setTouched(true);
  }

  return (
    <TextField
      label="Título del Artículo"
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={isError || existingArticle !== undefined}
      helperText={
        isError ? ' El título del Artículo no puede estar vacío '  
        : existingArticle !== undefined ? 'Este artículo ya exite, en la categoría selecionada en el campo "Seleccionar Categoría Existente"' : ''
      }
      fullWidth
    />
  );
}

export default SelectNewArticleTitle;
